import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Settings,
  FileText,
  Newspaper,
  Target,
  Power,
  Clock,
  Sparkles,
  Check,
  Loader2,
  Brain,
  Bitcoin,
  Globe,
  Zap,
  TrendingUp,
  Briefcase,
  Heart,
  LayoutDashboard,
  Crown,
  Coins,
  Infinity,
  User,
  CreditCard,
  Calendar,
  History,
  ChevronRight,
  ArrowRight,
  Shield,
  Upload,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { Alert, LoadingSpinner, FileUpload } from '@/components/ui';
import { SpotlightCard } from '@/components/verdict/SpotlightCard';
import { DeliveryScheduleManager } from '@/components/DeliveryScheduleManager';
import { usePreferences } from '@/hooks/usePreferences';
import { usePresets } from '@/hooks/usePresets';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import { ProfileTab } from '@/components/settings/ProfileTab';
import type { UploadedFile } from '@/types';

type SettingsTab = 'general' | 'newsletter' | 'profile';

// Map preset names to Lucide icons
const PRESET_ICON_MAP: Record<string, React.ElementType> = {
  'AI Research': Brain,
  'Crypto': Bitcoin,
  'Geopolitics': Globe,
  'Tech News': Zap,
  'Finance': TrendingUp,
  'Business': Briefcase,
  'Health': Heart,
  'General': Newspaper,
};

function getPresetIcon(presetName: string): React.ElementType {
  return PRESET_ICON_MAP[presetName] || Target;
}

export function PreferencesPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    initialTab === 'profile' ? 'profile' : initialTab === 'newsletter' ? 'newsletter' : 'general'
  );
  const { preferences, isLoading, savePreferences, toggleStatus } = usePreferences();
  const { presets, isLoading: presetsLoading } = usePresets();
  const { user, refreshUser, logout } = useAuth();
  const { balance, isUnlimited, isPaidUser, isLoading: creditsLoading } = useCredits();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');

  // File upload state
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);

  const loadFiles = async () => {
    try {
      const data = await api.getFiles();
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (preferences) {
      setSelectedPreset(preferences.preset_id || null);
      setCustomPrompt(preferences.custom_prompt || '');
    }
  }, [preferences]);

  useEffect(() => {
    if (user) {
      loadFiles();
    }
  }, [user]);

  const handleFileUpload = async (file: File) => {
    await api.uploadFile(file);
    await loadFiles();
    await refreshUser();
  };

  const handleFileDelete = async (fileId: string) => {
    await api.deleteFile(fileId);
    await loadFiles();
    await refreshUser();
  };

  const handleSaveNewsletter = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      await savePreferences({
        preset_id: selectedPreset || undefined,
        custom_prompt: customPrompt || undefined,
        delivery_time: preferences?.delivery_time || '08:00',
        timezone: preferences?.timezone || 'America/New_York',
        is_active: preferences?.is_active ?? true,
      });
      setMessage({ type: 'success', text: 'Newsletter settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const hasNewsletterChanges =
    selectedPreset !== (preferences?.preset_id || null) ||
    customPrompt !== (preferences?.custom_prompt || '');

  const handleToggleStatus = async () => {
    try {
      await toggleStatus(!preferences?.is_active);
      setMessage({
        type: 'success',
        text: preferences?.is_active ? 'Auto newsletter paused.' : 'Auto newsletter activated.',
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  if (isLoading || presetsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs: Array<{ id: SettingsTab; label: string; icon: React.ElementType; badge?: string }> = [
    { id: 'general', label: 'General', icon: LayoutDashboard },
    {
      id: 'newsletter',
      label: 'Auto Newsletter',
      icon: Newspaper,
      badge: preferences?.is_active ? 'ON' : 'OFF',
    },
    { id: 'profile', label: 'Profile', icon: User },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Premium Header */}
      <div className="relative">
        {/* Ambient glow behind header */}
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-verdict-primary/8 blur-[80px] rounded-full pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-verdict-primary/15 rounded-xl border border-verdict-primary/20 flex-shrink-0">
              <Settings className="w-5 h-5 text-verdict-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text-primary tracking-tight leading-tight">Settings</h1>
              <p className="text-text-secondary text-sm sm:text-base mt-0.5">
                Configure your Vettis research experience
              </p>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Premium Tab Bar */}
      <div className="flex gap-1.5 p-1.5 rounded-2xl bg-surface-light/50 border border-border-subtle backdrop-blur-sm">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-300',
                isActive
                  ? 'bg-gradient-to-b from-verdict-primary/20 to-verdict-primary/10 text-verdict-primary border border-verdict-primary/25 shadow-[0_2px_12px_rgba(212,165,116,0.15)]'
                  : 'text-text-muted hover:text-text-primary hover:bg-surface-light'
              )}
            >
              <Icon className={cn('w-4 h-4', isActive && 'drop-shadow-[0_0_4px_rgba(212,165,116,0.5)]')} />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.id === 'newsletter' ? 'Newsletter' : tab.id === 'profile' ? 'Profile' : tab.label}</span>
              {tab.badge && (
                <span
                  className={cn(
                    'px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest',
                    tab.badge === 'ON'
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                      : 'bg-surface-light/80 text-text-muted border border-border/50'
                  )}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <GeneralTab
          files={files}
          filesLoading={filesLoading}
          user={user}
          balance={balance}
          isUnlimited={isUnlimited}
          isPaidUser={isPaidUser}
          creditsLoading={creditsLoading}
          onUpload={handleFileUpload}
          onDelete={handleFileDelete}
          onNavigate={(path) => navigate(path)}
          onSwitchTab={setActiveTab}
        />
      )}

      {activeTab === 'profile' && (
        <ProfileTab
          user={user}
          refreshUser={refreshUser}
          logout={logout}
        />
      )}

      {activeTab === 'newsletter' && (
        <NewsletterTab
          preferences={preferences}
          presets={presets}
          selectedPreset={selectedPreset}
          customPrompt={customPrompt}
          isSaving={isSaving}
          hasChanges={hasNewsletterChanges}
          onPresetSelect={(id) => {
            setSelectedPreset(id);
            setCustomPrompt('');
          }}
          onCustomPromptChange={(val) => {
            setCustomPrompt(val);
            if (val) setSelectedPreset(null);
          }}
          onSave={handleSaveNewsletter}
          onToggleStatus={handleToggleStatus}
          onNavigate={navigate}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  General Tab                                                        */
/* ------------------------------------------------------------------ */

interface GeneralTabProps {
  files: UploadedFile[];
  filesLoading: boolean;
  user: ReturnType<typeof useAuth>['user'];
  balance: number | null;
  isUnlimited: boolean;
  isPaidUser: boolean;
  creditsLoading: boolean;
  onUpload: (file: File) => Promise<void>;
  onDelete: (fileId: string) => Promise<void>;
  onNavigate: (path: string) => void;
  onSwitchTab: (tab: SettingsTab) => void;
}

function GeneralTab({
  files,
  filesLoading,
  user,
  balance,
  isUnlimited,
  isPaidUser,
  creditsLoading,
  onUpload,
  onDelete,
  onNavigate,
  onSwitchTab,
}: GeneralTabProps) {
  const getPlanLabel = () => {
    const status = user?.subscription_status || 'trial';
    if (user?.is_master_user) return 'Master';
    const labels: Record<string, string> = {
      trial: 'Free Trial',
      active: isPaidUser ? 'Paid' : 'Active',
      basic: 'Basic',
      standard: 'Standard',
      professional: 'Professional',
      enterprise: 'Enterprise',
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = () => {
    const status = user?.subscription_status || 'trial';
    if (user?.is_master_user) return 'text-verdict-primary';
    if (status === 'active') return 'text-emerald-400';
    if (status === 'trial') return 'text-amber-400';
    return 'text-text-secondary';
  };

  const getStatusBg = () => {
    const status = user?.subscription_status || 'trial';
    if (user?.is_master_user) return 'bg-verdict-primary/10 border-verdict-primary/20';
    if (status === 'active') return 'bg-emerald-500/10 border-emerald-500/20';
    if (status === 'trial') return 'bg-amber-500/10 border-amber-500/20';
    return 'bg-surface-light/50 border-border/30';
  };

  const getBalanceColor = () => {
    if (isUnlimited) return 'text-verdict-primary';
    if (balance === null) return 'text-text-secondary';
    if (balance >= 1000) return 'text-emerald-400';
    if (balance >= 200) return 'text-amber-400';
    if (balance > 0) return 'text-orange-400';
    return 'text-red-400';
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="space-y-6">
      {/* Account Overview Card - Premium SpotlightCard */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15">
              <User className="w-5 h-5 text-verdict-primary" />
            </div>
            <div>
              <h2 className="text-lg font-serif font-semibold text-text-primary">Account Overview</h2>
              <p className="text-xs text-text-muted">Your Vettis account at a glance</p>
            </div>
          </div>
          {user?.is_master_user && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-verdict-primary/10 border border-verdict-primary/20 rounded-lg">
              <Shield className="w-3.5 h-3.5 text-verdict-primary" />
              <span className="text-xs font-semibold text-verdict-primary uppercase tracking-wider">Master</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {/* Plan */}
          <div className={cn(
            'rounded-xl border p-4 transition-all duration-200 hover:scale-[1.02]',
            getStatusBg()
          )}>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-text-secondary" />
              <span className="text-[11px] font-medium text-text-secondary uppercase tracking-wider">Plan</span>
            </div>
            <p className={cn('text-base font-bold', getStatusColor())}>
              {getPlanLabel()}
            </p>
            {user?.subscription_status === 'trial' && user.trial_days_remaining > 0 && (
              <p className="text-[11px] text-text-muted mt-1">
                {user.trial_days_remaining} day{user.trial_days_remaining !== 1 ? 's' : ''} remaining
              </p>
            )}
          </div>

          {/* Credits */}
          <div className="rounded-xl bg-surface-light/50 border border-border-subtle p-4 transition-all duration-200 hover:scale-[1.02] hover:border-border-subtle">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="w-4 h-4 text-text-secondary" />
              <span className="text-[11px] font-medium text-text-secondary uppercase tracking-wider">Credits</span>
            </div>
            {creditsLoading ? (
              <div className="h-6 w-20 bg-surface-light rounded-lg animate-pulse" />
            ) : isUnlimited ? (
              <div className="flex items-center gap-1.5">
                <Infinity className="w-5 h-5 text-verdict-primary" />
                <span className="text-base font-bold text-verdict-primary">Unlimited</span>
              </div>
            ) : (
              <p className={cn('text-base font-bold tabular-nums', getBalanceColor())}>
                {balance !== null ? balance.toLocaleString() : '--'}
              </p>
            )}
          </div>

          {/* Files */}
          <div className="rounded-xl bg-surface-light/50 border border-border-subtle p-4 transition-all duration-200 hover:scale-[1.02] hover:border-border-subtle">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-text-secondary" />
              <span className="text-[11px] font-medium text-text-secondary uppercase tracking-wider">Files</span>
            </div>
            <p className="text-base font-bold text-text-primary tabular-nums">
              {user?.files_count || 0}
              <span className="text-text-muted font-normal text-sm">
                {' / '}{user?.max_files === null ? 'unlimited' : (user?.max_files || 3)}
              </span>
            </p>
          </div>

          {/* Member Since */}
          <div className="rounded-xl bg-surface-light/50 border border-border-subtle p-4 transition-all duration-200 hover:scale-[1.02] hover:border-border-subtle">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-text-secondary" />
              <span className="text-[11px] font-medium text-text-secondary uppercase tracking-wider">Member Since</span>
            </div>
            <p className="text-base font-bold text-text-primary">
              {memberSince || '--'}
            </p>
          </div>
        </div>

        {/* Quick Actions - Premium buttons */}
        <div className="flex flex-wrap gap-2.5 pt-4 border-t border-border-subtle">
          <button
            onClick={() => onNavigate('/dashboard/plans')}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200',
              'bg-surface-light border border-border-subtle text-text-primary',
              'hover:bg-surface-raised hover:border-border-subtle hover:text-text-primary hover:shadow-lg hover:shadow-white/5'
            )}
          >
            <CreditCard className="w-3.5 h-3.5" />
            Manage Plan
            <ArrowRight className="w-3 h-3 opacity-50" />
          </button>
          {!isUnlimited && (
            <button
              onClick={() => onNavigate('/dashboard/plans')}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200',
                'bg-gradient-to-r from-verdict-primary/15 to-verdict-primary/5 border border-verdict-primary/20 text-verdict-primary',
                'hover:from-verdict-primary/20 hover:to-verdict-primary/10 hover:border-verdict-primary/30 hover:shadow-lg hover:shadow-verdict-primary/10'
              )}
            >
              <Coins className="w-3.5 h-3.5" />
              Buy Credits
              <ArrowRight className="w-3 h-3 opacity-50" />
            </button>
          )}
          <button
            onClick={() => onSwitchTab('profile')}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200',
              'bg-surface-light border border-border-subtle text-text-primary',
              'hover:bg-surface-raised hover:border-border-subtle hover:text-text-primary hover:shadow-lg hover:shadow-white/5'
            )}
          >
            <User className="w-3.5 h-3.5" />
            Edit Profile
            <ArrowRight className="w-3 h-3 opacity-50" />
          </button>
        </div>
      </SpotlightCard>

      {/* Theme Card */}
      <ThemeCard />

      {/* Context Files Card - Premium SpotlightCard */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15">
              <FileText className="w-5 h-5 text-verdict-primary" />
            </div>
            <div>
              <h2 className="text-lg font-serif font-semibold text-text-primary flex items-center gap-1.5">Context Files <InfoTooltip content="Upload documents that AI references during every research session — company reports, meeting notes, or domain-specific data." position="right" size="md" /></h2>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-light border border-border-subtle rounded-lg">
            <Upload className="w-3.5 h-3.5 text-text-secondary" />
            <span className="text-xs font-medium text-text-secondary tabular-nums">
              {user?.files_count || 0} / {user?.max_files === null ? 'unlimited' : (user?.max_files || 3)}
            </span>
          </div>
        </div>
        <p className="text-sm text-text-secondary mb-5 ml-[52px]">
          Upload documents to enhance research quality across all Vettis features.
        </p>

        <FileUpload
          files={files}
          onUpload={onUpload}
          onDelete={onDelete}
          maxFiles={user?.max_files === undefined ? 3 : user.max_files}
          isLoading={filesLoading}
        />
      </SpotlightCard>

      {/* Helpful tip - Premium styled */}
      <div className="rounded-2xl bg-gradient-to-r from-verdict-primary/[0.06] to-transparent border border-verdict-primary/10 p-5">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-verdict-primary/15 rounded-lg mt-0.5">
            <Sparkles className="w-3.5 h-3.5 text-verdict-primary" />
          </div>
          <p className="text-sm text-text-primary leading-relaxed">
            <span className="font-semibold text-verdict-primary">Pro Tip</span>{' '}
            <span className="text-text-muted mx-1">--</span>
            Upload research papers, company reports, or reference material. Vettis agents will use these
            to deliver deeper, more contextual analysis in every research query.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Auto Newsletter Tab                                                */
/* ------------------------------------------------------------------ */

interface NewsletterTabProps {
  preferences: ReturnType<typeof usePreferences>['preferences'];
  presets: Array<{ id: string; name: string; description: string; icon: string; category: string; prompt_template: string }>;
  selectedPreset: string | null;
  customPrompt: string;
  isSaving: boolean;
  hasChanges: boolean;
  onPresetSelect: (id: string) => void;
  onCustomPromptChange: (val: string) => void;
  onSave: () => void;
  onToggleStatus: () => void;
  onNavigate: (path: string) => void;
}

function NewsletterTab({
  preferences,
  presets,
  selectedPreset,
  customPrompt,
  isSaving,
  hasChanges,
  onPresetSelect,
  onCustomPromptChange,
  onSave,
  onToggleStatus,
  onNavigate,
}: NewsletterTabProps) {
  const isActive = preferences?.is_active ?? false;

  return (
    <div className="space-y-6">
      {/* Status Toggle Card - Premium */}
      <SpotlightCard className="rounded-2xl" contentClassName="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-300',
                isActive
                  ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/25'
                  : 'bg-surface-light/80 border-border/50'
              )}
            >
              <Power className={cn('w-5 h-5 transition-colors', isActive ? 'text-emerald-400' : 'text-text-muted')} />
            </div>
            <div>
              <p className="font-serif font-semibold text-text-primary text-lg flex items-center gap-1.5">Auto Newsletter <InfoTooltip content="When active, Vettis automatically researches your chosen topics and emails you a personalized newsletter on your selected schedule." position="right" size="md" /></p>
              <p className="text-sm text-text-muted mt-0.5">
                {isActive
                  ? 'Vettis automatically researches and emails you on schedule.'
                  : 'Enable to receive automated daily research newsletters.'}
              </p>
            </div>
          </div>
          <button
            onClick={onToggleStatus}
            className={cn(
              'relative w-14 h-8 rounded-full transition-all duration-300 flex-shrink-0',
              isActive
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_12px_rgba(34,197,94,0.3)]'
                : 'bg-surface-raised/80'
            )}
          >
            <span
              className={cn(
                'absolute top-1.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300',
                isActive ? 'translate-x-7' : 'translate-x-1.5'
              )}
            />
          </button>
        </div>
      </SpotlightCard>

      {/* Newsletter Configuration */}
      <div className={cn('transition-all duration-300', !isActive && 'opacity-50 pointer-events-none select-none')}>
        {/* Topic Selection */}
        <SpotlightCard className="rounded-2xl mb-6" contentClassName="p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-lg border border-verdict-primary/15">
              <Target className="w-4 h-4 text-verdict-primary" />
            </div>
            <h2 className="text-lg font-serif font-semibold text-text-primary">Research Topic</h2>
          </div>
          <p className="text-sm text-text-secondary mb-5 ml-[44px]">
            Choose a preset topic or write a custom prompt.
          </p>

          {/* Preset Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-5">
            {presets.map((preset) => {
              const PresetIcon = getPresetIcon(preset.name);
              const isSelected = selectedPreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => onPresetSelect(preset.id)}
                  className={cn(
                    'p-3.5 rounded-xl border text-center transition-all duration-200 hover:scale-[1.02]',
                    isSelected
                      ? 'border-verdict-primary/50 bg-gradient-to-b from-verdict-primary/15 to-verdict-primary/5 shadow-[0_2px_12px_rgba(212,165,116,0.12)]'
                      : 'border-border-subtle bg-surface-light/30 hover:border-border-subtle hover:bg-surface-light'
                  )}
                >
                  <div className="flex justify-center mb-2">
                    <div className={cn(
                      'p-2 rounded-lg transition-colors',
                      isSelected ? 'bg-verdict-primary/15' : 'bg-surface-light'
                    )}>
                      <PresetIcon
                        className={cn(
                          'w-4 h-4',
                          isSelected ? 'text-verdict-primary' : 'text-text-secondary'
                        )}
                      />
                    </div>
                  </div>
                  <p className={cn('text-xs font-medium truncate', isSelected ? 'text-verdict-primary' : 'text-text-primary')}>{preset.name}</p>
                </button>
              );
            })}
          </div>

          {/* Custom Prompt */}
          <div className="border-t border-border-subtle pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-verdict-primary" />
              <span className="text-sm font-medium text-text-primary">Or write a custom prompt</span>
              {customPrompt && !selectedPreset && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-verdict-primary/15 text-verdict-primary border border-verdict-primary/20">
                  Active
                </span>
              )}
            </div>
            <textarea
              value={customPrompt}
              onChange={(e) => onCustomPromptChange(e.target.value)}
              placeholder="e.g. Research the latest developments in quantum computing, focusing on practical applications..."
              rows={3}
              className="w-full px-4 py-3 bg-surface-light/50 border border-border-subtle rounded-xl text-text-primary placeholder-text-muted text-sm resize-none focus:outline-none focus:border-verdict-primary/40 focus:bg-surface-light transition-all duration-200"
            />
            <p className="text-xs text-text-muted mt-2">
              Be specific about topics, timeframes, or aspects you want covered.
            </p>
          </div>
        </SpotlightCard>

        {/* Delivery Schedule */}
        <SpotlightCard className="rounded-2xl mb-6" contentClassName="p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-lg border border-verdict-primary/15">
              <Clock className="w-4 h-4 text-verdict-primary" />
            </div>
            <h2 className="text-lg font-serif font-semibold text-text-primary">Delivery Schedule</h2>
          </div>
          <p className="text-sm text-text-secondary mb-5 ml-[44px]">
            Set when Vettis should generate and email your newsletter.
          </p>
          <div className="verdict-settings-override">
            <DeliveryScheduleManager />
          </div>
          <div className="mt-4 flex items-center gap-2.5 text-xs text-text-muted px-4 py-2.5 rounded-xl bg-surface-light/50 border border-border-subtle">
            <Clock className="w-3.5 h-3.5 text-text-muted" />
            <span>Reports typically take 5-20 minutes to generate.</span>
          </div>
        </SpotlightCard>

        {/* Newsletter Pages */}
        <SpotlightCard className="rounded-2xl mb-6" contentClassName="p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-lg border border-verdict-primary/15">
              <Newspaper className="w-4 h-4 text-verdict-primary" />
            </div>
            <h2 className="text-lg font-serif font-semibold text-text-primary">Newsletter Data</h2>
          </div>
          <p className="text-sm text-text-secondary mb-4 ml-[44px]">
            View generated newsletters and collected research insights.
          </p>
          <div className="space-y-2.5">
            <button
              onClick={() => onNavigate('/history')}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200',
                'bg-surface-light/50 border border-border-subtle hover:bg-surface-light hover:border-border-subtle group'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-surface-light rounded-lg group-hover:bg-surface-raised transition-colors">
                  <History className="w-4 h-4 text-text-secondary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary">Newsletter History</p>
                  <p className="text-xs text-text-muted">Past newsletters generated and emailed to you</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-text-secondary group-hover:translate-x-0.5 transition-all" />
            </button>
            <button
              onClick={() => onNavigate('/research')}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200',
                'bg-surface-light/50 border border-border-subtle hover:bg-surface-light hover:border-border-subtle group'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-surface-light rounded-lg group-hover:bg-surface-raised transition-colors">
                  <Brain className="w-4 h-4 text-text-secondary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary">Research Insights</p>
                  <p className="text-xs text-text-muted">Topics, entities, and insights from newsletter research</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-text-secondary group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
        </SpotlightCard>

        {/* Save Button - Premium gradient */}
        {hasChanges && (
          <div className="flex justify-end">
            <button
              onClick={onSave}
              disabled={isSaving}
              className={cn(
                'px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2',
                'bg-gradient-to-r from-verdict-primary to-verdict-primary-hover text-black',
                'hover:shadow-[0_4px_20px_rgba(212,165,116,0.3)]',
                isSaving && 'opacity-70 cursor-not-allowed'
              )}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Inactive overlay hint - Premium */}
      {!isActive && (
        <div className="rounded-2xl bg-gradient-to-r from-surface-light/30 to-transparent border border-border-subtle p-5 text-center">
          <p className="text-sm text-text-muted">
            Enable the toggle above to configure and start receiving automated newsletters.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Theme Card                                                         */
/* ------------------------------------------------------------------ */

const THEME_OPTIONS = [
  { id: 'light' as const, label: 'Light', icon: Sun, description: 'Warm off-white background' },
  { id: 'dark' as const, label: 'Dark', icon: Moon, description: 'Dark background' },
  { id: 'system' as const, label: 'System', icon: Monitor, description: 'Match your OS setting' },
];

function ThemeCard() {
  const { mode, setMode } = useTheme();

  return (
    <SpotlightCard className="rounded-2xl" contentClassName="p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2.5 bg-gradient-to-br from-verdict-primary/20 to-verdict-primary/5 rounded-xl border border-verdict-primary/15">
          <Sun className="w-5 h-5 text-verdict-primary" />
        </div>
        <div>
          <h2 className="text-lg font-serif font-semibold text-text-primary">Theme</h2>
          <p className="text-xs text-text-muted">Choose how Vettis looks to you</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = mode === option.id;
          return (
            <button
              key={option.id}
              onClick={() => setMode(option.id)}
              className={cn(
                'p-4 rounded-xl border text-center transition-all duration-200 hover:scale-[1.02]',
                isSelected
                  ? 'border-verdict-primary/50 bg-gradient-to-b from-verdict-primary/15 to-verdict-primary/5 shadow-[0_2px_12px_rgba(212,165,116,0.12)]'
                  : 'border-border-subtle bg-surface-light/30 hover:border-border-subtle hover:bg-surface-light'
              )}
            >
              <div className="flex justify-center mb-2">
                <div className={cn(
                  'p-2 rounded-lg transition-colors',
                  isSelected ? 'bg-verdict-primary/15' : 'bg-surface-light'
                )}>
                  <Icon
                    className={cn(
                      'w-5 h-5',
                      isSelected ? 'text-verdict-primary' : 'text-text-secondary'
                    )}
                  />
                </div>
              </div>
              <p className={cn('text-sm font-medium', isSelected ? 'text-verdict-primary' : 'text-text-primary')}>
                {option.label}
              </p>
              <p className="text-[10px] text-text-muted mt-0.5">{option.description}</p>
            </button>
          );
        })}
      </div>
    </SpotlightCard>
  );
}

